/**
 * Platform Audit Log Service
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from '../config/api';
import type {
  PaginatedPlatformAuditLogs,
  PlatformAuditAction,
  PlatformAuditLog,
} from '../types';

export interface FindPlatformAuditLogsQuery {
  action?: PlatformAuditAction;
  targetTenantId?: string;
  performedBy?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Per-entity-type breakdown stored on every TEMPLATE_SEED / INDUSTRY_PACK_SEED
 * audit entry's `details.perEntityType`. Mirrors the backend shape produced by
 * `TemplateSeedingService.buildAuditDetail`. Keys are TemplateEntityType
 * values (e.g. `'checklist_groups'`) and each entry carries the per-item
 * arrays so the admin UI can render the natural-key listing on expand.
 */
export interface SeedAuditPerEntityType {
  created: Array<{ naturalKey: Record<string, unknown>; reason?: string }>;
  updated: Array<{ naturalKey: Record<string, unknown>; reason?: string }>;
  skipped: Array<{ naturalKey: Record<string, unknown>; reason?: string }>;
}

/**
 * Typed view of the `details` JSON on TEMPLATE_SEED / INDUSTRY_PACK_SEED
 * audit entries. Callers may still receive entries created before any field
 * was added, so every member is treated as optional for forward compatibility.
 */
export interface SeedAuditDetails {
  source?: string;
  packVersion?: number;
  environment?: string;
  dryRun?: boolean;
  conflictStrategy?: string;
  perEntityType?: Record<string, SeedAuditPerEntityType>;
}

export const auditService = {
  /**
   * List platform audit log entries (paginated, newest first).
   *
   * Used by the platform admin UI to surface "who did X and when" for
   * critical operations like feature-flag overrides.
   */
  async findPlatformAuditLogs(
    query: FindPlatformAuditLogsQuery = {}
  ): Promise<PaginatedPlatformAuditLogs> {
    const response = await apiClient.get<PaginatedPlatformAuditLogs>(
      API_ENDPOINTS.PLATFORM_AUDIT_LOGS,
      { params: query }
    );
    return response.data;
  },

  /**
   * Fetch INDUSTRY_PACK_SEED audit entries for a specific tenant.
   *
   * Used by the per-tenant industry-pack section to derive each pack's
   * "Installert <date> av <operator>" indicator from the most recent
   * non-dry-run install. The backend keeps audit entries in newest-first
   * order, so callers can iterate the result and pick the first match per
   * `details.source`.
   */
  async getPackInstallEntries(
    targetTenantId: string,
    pageSize = 50
  ): Promise<PlatformAuditLog[]> {
    const response = await apiClient.get<PaginatedPlatformAuditLogs>(
      API_ENDPOINTS.PLATFORM_AUDIT_LOGS,
      {
        params: {
          action: 'INDUSTRY_PACK_SEED' satisfies PlatformAuditAction,
          targetTenantId,
          pageSize,
        },
      }
    );
    return response.data.results;
  },

  /**
   * Fetch every seed-related audit entry (master + pack) for a tenant.
   *
   * Two requests run in parallel because the backend audit-log endpoint
   * accepts a single `action` filter. Results are merged and sorted newest
   * first so the section can render an "Installeringshistorikk" list with
   * the same expand-toggle for both action types.
   */
  async getSeedAuditEntries(
    targetTenantId: string,
    pageSize = 50
  ): Promise<PlatformAuditLog[]> {
    const [packs, master] = await Promise.all([
      apiClient.get<PaginatedPlatformAuditLogs>(
        API_ENDPOINTS.PLATFORM_AUDIT_LOGS,
        {
          params: {
            action: 'INDUSTRY_PACK_SEED' satisfies PlatformAuditAction,
            targetTenantId,
            pageSize,
          },
        }
      ),
      apiClient.get<PaginatedPlatformAuditLogs>(
        API_ENDPOINTS.PLATFORM_AUDIT_LOGS,
        {
          params: {
            action: 'TEMPLATE_SEED' satisfies PlatformAuditAction,
            targetTenantId,
            pageSize,
          },
        }
      ),
    ]);
    return [...packs.data.results, ...master.data.results].sort(
      (a, b) =>
        new Date(b.performedAt).getTime() - new Date(a.performedAt).getTime()
    );
  },
};
